if (!customElements.get("description-toggle")) {
  class DescriptionToggle extends HTMLElement {
    constructor() {
      super();
      this.initializeElements();
      this.initializeEventListeners();
      this.checkContentOverflow();
    }

    initializeElements() {
      this.content = this.querySelector(".description-content");
      this.viewMoreBtn = this.querySelector(".view-more-btn");
      this.viewLessBtn = this.querySelector(".view-less-btn");
      this.removeEmptyListItems();
      this.makeLabelsBold();
    }

    removeEmptyListItems() {
      if (!this.content) return;

      // Find all list items within the description content
      const listItems = this.content.querySelectorAll("li");

      listItems.forEach((li) => {
        // Get text content and normalise:
        // - Convert non‑breaking spaces (&nbsp;) to regular spaces
        // - Collapse whitespace and trim
        const rawText = li.textContent || "";
        const normalizedText = rawText
          .replace(/\u00A0/g, " ")
          .replace(/\s+/g, " ")
          .trim();

        const lower = normalizedText.toLowerCase();

        // Treat as "empty" if:
        // - No text after normalisation, OR
        // - It only contains the literal ::marker (common RTE artefact)
        if (!lower || lower === "::marker") {
          li.style.display = "none";
        }
      });
    }

    makeLabelsBold() {
      if (!this.content || this.content.dataset.processed === "true") return;

      // Mark as processed to avoid re-processing
      this.content.dataset.processed = "true";

      // Collect all text nodes first
      const textNodes = [];
      const walker = document.createTreeWalker(
        this.content,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            // Skip text nodes inside script, style, or already processed elements
            const parent = node.parentElement;
            if (
              parent &&
              (parent.tagName === "SCRIPT" || parent.tagName === "STYLE")
            ) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          },
        },
        false,
      );

      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.trim()) {
          textNodes.push(node);
        }
      }

      // Helper function to check if a text node is at the start of its parent element
      const isFirstTextInParent = (textNode) => {
        const parent = textNode.parentNode;
        if (!parent) return false;

        // Check if this is the first text node in the parent
        let node = parent.firstChild;
        while (node) {
          if (node === textNode) return true;
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            return false; // Found another text node before this one
          }
          node = node.nextSibling;
        }
        return false;
      };

      // Process each text node
      textNodes.forEach((textNode) => {
        const text = textNode.textContent;
        const isFirstText = isFirstTextInParent(textNode);

        // Build regex pattern:
        // 1. If at start of element, match from beginning up to the first colon: ^([^:]+?):
        // 2. Match after punctuation (. ! ?) followed by spaces: ([\\.!?]\s+)([^:]+?):
        // 3. Match after newline: ([\n\r]+\s*)([^:]+?):
        let regex;
        if (isFirstText) {
          // Include pattern for start of text node
          regex = /(?:^([^:]+?):|([\\.!?]\s+|[\n\r]+\s*)([^:]+?):)/g;
        } else {
          // Only match after punctuation or newline
          regex = /([\\.!?]\s+|[\n\r]+\s*)([^:]+?):/g;
        }

        const matches = [...text.matchAll(regex)];

        if (matches.length > 0) {
          const parent = textNode.parentNode;
          let lastIndex = 0;
          const fragments = [];

          matches.forEach((match) => {
            // Add text before the match
            if (match.index > lastIndex) {
              fragments.push(
                document.createTextNode(text.substring(lastIndex, match.index)),
              );
            }

            // Determine which capture group matched
            let label;
            if (isFirstText && match[1]) {
              // Matched from start of text node
              label = match[1].trim();
              // No preceding punctuation/newline to add
            } else {
              // Matched after punctuation or newline
              fragments.push(
                document.createTextNode(match[isFirstText ? 2 : 1]),
              );
              label = match[isFirstText ? 3 : 2].trim();
            }

            // Create bold span for text until colon
            const span = document.createElement("span");
            span.className = "font-bold";
            span.textContent = label + ":";
            fragments.push(span);

            lastIndex = match.index + match[0].length;
          });

          // Add remaining text after last match
          if (lastIndex < text.length) {
            fragments.push(document.createTextNode(text.substring(lastIndex)));
          }

          // Replace the original text node with fragments
          if (fragments.length > 0) {
            fragments.forEach((fragment) => {
              parent.insertBefore(fragment, textNode);
            });
            parent.removeChild(textNode);
          }
        }
      });
    }

    initializeEventListeners() {
      this.viewMoreBtn?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleDescription(true);
      });
      this.viewLessBtn?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleDescription(false);
      });
    }

    checkContentOverflow() {
      if (!this.content || !this.viewMoreBtn) return;

      const scroll = this.content.scrollHeight;
      const height = this.content.offsetHeight;

      // Show the button if there is clear overflow OR as a fallback when
      // we can't reliably detect it (e.g. line-clamp + -webkit-box tricks).
      const hasOverflow = scroll - height > 2;

      if (hasOverflow || this.content.textContent.trim().length > 200) {
        this.viewMoreBtn.classList.remove("hidden");
      }
    }

    toggleDescription(showMore) {
      this.content.classList.toggle("line-clamp-6", !showMore);
      this.viewMoreBtn?.classList.toggle("hidden", showMore);
      this.viewLessBtn?.classList.toggle("hidden", !showMore);
    }
  }

  customElements.define("description-toggle", DescriptionToggle);
}
